import React from 'react'
/** REACT ROUTER */
import AppRouter from './routes'
import { AuthProvider } from './hooks/auth';
import { SocketProvider } from './hooks/socket';
import './App.css';
/** Material theme */
import { ThemeProvider, createTheme, Palette as MUIPalette, TypeBackground } from '@mui/material/styles'
import type {} from '@mui/x-data-grid/themeAugmentation'
/** SNACKBAR */
import { SnackbarProvider } from 'notistack'
/** COLORS */
import { Default, Pink, Green, Blue, Purple, Fuchsia } from './layout/colors'
/** FONTS */
import '@fontsource/roboto/300.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';
import '@fontsource/open-sans/300.css'
import '@fontsource/open-sans/400.css'
import '@fontsource/open-sans/600.css'
import '@fontsource/open-sans/500.css'
import '@fontsource/open-sans/700.css'
import '@fontsource/noto-sans/300.css'
import '@fontsource/noto-sans/400.css'
import '@fontsource/noto-sans/600.css'
import '@fontsource/noto-sans/700.css'
import '@fontsource/noto-serif/300.css'
import '@fontsource/noto-serif/400.css'
import '@fontsource/noto-serif/600.css'
import '@fontsource/noto-serif/700.css'

const originalFetch = window.fetch

const scheme =
	localStorage.getItem('colorPalette') === 'default'
		? Default
		: localStorage.getItem('colorPalette') === 'pink'
		? Pink
		: localStorage.getItem('colorPalette') === 'green'
		? Green
		: localStorage.getItem('colorPalette') === 'blue'
		? Blue
		: localStorage.getItem('colorPalette') === 'purple'
		? Purple
		: Fuchsia

window.fetch = async (url: RequestInfo | URL, init?: RequestInit) => {
	// add token from local storage to headers
	const token = localStorage.getItem('token')

	const headers = init?.headers ? new Headers(init?.headers) : new Headers()

	// if init doesn't include credentials, set it to 'include'
	if (!init?.credentials) {
		init = { ...init, credentials: 'include' }
	}

	if (token) {
		headers.set('Authorization', `Bearer ${token}`)
	}

	return originalFetch(url, { ...init, headers })
}

// add new color to mui palette
declare module '@mui/material/styles' {
	interface TypeBackground {
		secondary?: string
	}
	interface Palette {
		black: {
			main: string
		}
		inputColor: {
			main: string
		}
		background: TypeBackground
	}
	interface PaletteOptions {
		black?: PaletteOptions['primary']
		inputColor?: PaletteOptions['primary']
		background?: Partial<TypeBackground>
	}
}

// add new color to mui button
declare module '@mui/material/Button' {
	interface ButtonPropsColorOverrides {
		black: true
		inputColor: true
	}
}

// add new variant to mui paper
declare module '@mui/material/Paper' {
	interface PaperPropsVariantOverrides {
		shadowed: true
	}
}

const theme = createTheme({
	typography: {
		fontFamily: localStorage.getItem('fontFamily') || `'Roboto', sans-serif`,
		fontSize: 14,
		h6: {
			fontSize: '1.05rem',
		},
	},
	palette: {
		primary: {
			main: scheme.light.primary,
		},
		secondary: {
			main: scheme.light.secondary,
		},
		black: {
			main: scheme.light.black,
		},
		inputColor: {
			main: scheme.light.inputColor,
		},
		background: {
			default: scheme.light.background.default,
			secondary: scheme.light.background.secondary,
		},
		divider: scheme.light.divider ?? 'rgba(0,0,0,0.02)',
	},
	components: {
		MuiChip: {
			styleOverrides: {
				colorWarning: {
					color: 'white',
					backgroundColor: '#ffb74d',
				},
				colorError: {
					color: 'white',
					backgroundColor: '#e91e63',
				},
				colorSuccess: {
					color: 'white',
					backgroundColor: '#558b2f',
				},
			},
		},
		MuiPaper: {
			styleOverrides: {
				rounded: {
					borderRadius: 8,
				},
				outlined: {
					borderColor: 'rgba(0,0,0,0.12)!important',
					//boxShadow: 'rgba(100, 100, 111, 0.2) 0px 7px 29px 0px;',
					borderWidth: 1,
				},
			},
			variants: [
				{
					props: { variant: 'shadowed' },
					style: {
						boxShadow: 'rgba(100, 100, 111, 0.2) 0px 7px 29px 0px;',
						borderWidth: 0,
					},
				},
			],
		},
	},
})

const darkTheme = createTheme({
	typography: {
		fontFamily: localStorage.getItem('fontFamily') || `'Roboto', sans-serif`,
		fontSize: 14,
	},
	palette: {
		mode: 'dark',
		black: {
			main: 'rgba(255,255,255,0.57)',
		},
		inputColor: {
			main: 'rgba(255,255,255,0.87)',
		},
		divider: 'rgba(255,255,255,0.08)',
		text: {
			primary: 'rgba(255,255,255,0.87)',
			secondary: 'rgba(255,255,255,0.6)',
			disabled: 'rgba(255,255,255,0.5)',
		},
		background: {
			default: '#0e1117',
			paper: 'rgb(24, 24, 24)',
		},
	},
	components: {
		MuiChip: {
			styleOverrides: {
				colorWarning: {
					color: 'white',
					backgroundColor: '#ffb74d',
				},
				colorError: {
					color: 'white',
					backgroundColor: '#e91e63',
				},
				colorSuccess: {
					color: 'white',
					backgroundColor: '#558b2f',
				},
			},
		},
		MuiTypography: {
			defaultProps: {
				color: 'textPrimary',
			},
		},
		MuiPaper: {
			styleOverrides: {
				root: {
					backgroundColor: '#15181d!important',
				},
				outlined: {
					border: 'none',
				},
			},
			variants: [
				{
					props: { variant: 'shadowed' },
					style: {},
				},
			],
		},
		MuiDataGrid: {
			styleOverrides: {
				row: {
					'--rowBorderColor': 'rgba(255,255,255,0.1)',
				},
				columnHeader: {
					'--rowBorderColor': 'rgba(255,255,255,0.1)',
				},
				root: {
					border: 'none',
				},
			},
		},
	},
})

export const ColorModeContext = React.createContext({ toggleColorMode: () => {} })

function App() {
	React.useEffect(() => {
		fetch(process.env.REACT_APP_API_URL + '/v1', {
			credentials: 'include',
		})
			.then((res) => res.json())
			.then((data) => {
				console.log(data)
			})
			.catch((err) => {
				console.error(err)
			})
	}, [])

	const [mode, setMode] = React.useState<'light' | 'dark'>(
		localStorage.getItem('colorMode') === 'dark' ? 'dark' : 'light'
	)

	const toggleColorMode = () => {
		// save mode to local storage
		localStorage.setItem('colorMode', mode === 'light' ? 'dark' : 'light')
		setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'))
	}

	return (
		<ColorModeContext.Provider
			value={{
				toggleColorMode,
			}}
		>
			<ThemeProvider theme={mode === 'light' ? theme : darkTheme}>
				<SnackbarProvider>
					<AuthProvider>
						<SocketProvider>
							<div className="App">
								<AppRouter />
							</div>
						</SocketProvider>
					</AuthProvider>
				</SnackbarProvider>
			</ThemeProvider>
		</ColorModeContext.Provider>
	)
}

export default App
